import { compact, get, isEmpty, size, startCase } from "lodash";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { trackPromise } from "react-promise-tracker";
import { useParams } from "react-router-dom";

import { Checkmark, Icon } from "@shared/ui";

import { ClientContext } from "../App";
import Callout from "../components/Callout";
import {
  DetailsPage,
  DetailsPageCard,
  DetailsPageColumn,
} from "../components/DetailsPage";
import LoadingSpinner from "../components/LoadingSpinner";
import Policy from "../components/Policy";
import PolicyValidation from "../components/PolicyValidation";
import {
  getAvailablePolicyActions,
  getPolicy,
  getUser,
  isPolicyActionInProgress,
  resumePendingPolicyActions,
  subscribeToPolicyAction,
} from "../lib/automatic";
import { SECTION_LOCATION, VALIDATION_LOCATION } from "../lib/constants";
import { handleForbiddenError } from "../lib/errorHandler";

const applyCalloutForPolicy = (fetchedPolicy, setCallout) => {
  if (get(fetchedPolicy, "banner")) {
    setCallout(fetchedPolicy.banner);
    return;
  }
  if (fetchedPolicy.isActive !== undefined) {
    setCallout(undefined);
    return;
  }
  if (
    !isEmpty(fetchedPolicy.insureds) ||
    !isEmpty(fetchedPolicy.expirationDate) ||
    !isEmpty(fetchedPolicy.policyNumber) ||
    !isEmpty(fetchedPolicy.documents)
  ) {
    setCallout({
      type: "warning",
      message:
        "Please note that this policy information was manually uploaded by the user and has not been verified through Axle.",
    });
    return;
  }
  setCallout({
    type: "warning",
    message:
      "Connection to the policy is still being established. Limited details available, please check back later for updates.",
  });
};

const POLICY_TERM_LABELS = {
  current: "Current",
  renewal: "Renewal",
};

const PolicyTermSelector = ({
  isActionInProgress,
  policyTerm,
  availableTerms,
  handleTermChange,
}) => {
  return (
    <div className="flex flex-row gap-3">
      {availableTerms.map((term) => (
        <button
          key={term}
          type="button"
          disabled={isActionInProgress}
          className={`flex-1 rounded border px-4 py-3 text-left text-sm transition ${
            policyTerm === term
              ? "border-black border-2"
              : "border-gray-200 hover:border-gray-400"
          } ${
            isActionInProgress
              ? "opacity-50 cursor-not-allowed"
              : "cursor-pointer"
          }`}
          onClick={() => handleTermChange(term)}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="font-semibold">{POLICY_TERM_LABELS[term]}</div>
            {policyTerm === term ? (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-black px-2.5 py-1 text-xs font-semibold text-white">
                <Icon
                  icon={Checkmark}
                  width="w-3"
                  height="h-3"
                  className="fill-white"
                />
                Selected
              </span>
            ) : null}
          </div>
          <div className="text-gray-600 text-xs mt-1">Policy term</div>
        </button>
      ))}
    </div>
  );
};

const PolicyDetails = () => {
  let params = useParams();
  const [policy, setPolicy] = useState();
  const [user, setUser] = useState();
  const [callout, setCallout] = useState();
  const [policyActions, setPolicyActions] = useState({
    validation: [],
    sectionsByName: {},
  });
  const [validationRules, setValidationRules] = useState({});

  const [isLoading, setIsLoading] = useState(true);
  const [isActionInProgress, setIsActionInProgress] = useState(() =>
    isPolicyActionInProgress(params.policyId)
  );
  const { clientConfig: client, selectedClient: clientId } =
    useContext(ClientContext);
  const [policyTerm, setPolicyTerm] = useState("current");
  const [currentPolicy, setCurrentPolicy] = useState();
  const [renewalPolicy, setRenewalPolicy] = useState(null);

  // A term whose policy came back 404 has no policy, so it gets no tab.
  const availableTerms = compact([
    currentPolicy && "current",
    renewalPolicy && "renewal",
  ]);

  const validationTriggerRef = useRef(null);
  const cachedValidationRulesRef = useRef({});
  const policyTermRef = useRef(policyTerm);
  policyTermRef.current = policyTerm;

  const handlePolicyRefreshSuccess = (updatedPolicy) => {
    setCurrentPolicy(updatedPolicy);
    if (policyTerm === "current") {
      setPolicy(updatedPolicy);
      applyCalloutForPolicy(updatedPolicy, setCallout);
      if (validationTriggerRef.current) {
        validationTriggerRef.current();
      }
    }
  };

  // Callback to receive validation results from PolicyValidation
  const handleValidationResults = useCallback((rules) => {
    cachedValidationRulesRef.current = rules;
    setValidationRules(rules);
  }, []);

  const handleTermChange = useCallback(
    (nextTerm) => {
      if (nextTerm === policyTerm) {
        return;
      }
      const nextPolicy = nextTerm === "renewal" ? renewalPolicy : currentPolicy;
      if (!nextPolicy) {
        return;
      }
      setPolicyTerm(nextTerm);
      setPolicy(nextPolicy);
      if (nextTerm === "current") {
        applyCalloutForPolicy(nextPolicy, setCallout);
        setValidationRules(cachedValidationRulesRef.current);
      } else {
        setCallout(undefined);
        setValidationRules({});
      }
    },
    [policyTerm, currentPolicy, renewalPolicy]
  );

  // Subscribe to module-level action-state changes.
  useEffect(() => {
    setIsActionInProgress(isPolicyActionInProgress(params.policyId));

    const unsubscribe = subscribeToPolicyAction(
      params.policyId,
      (inProgress) => {
        setIsActionInProgress(inProgress);
        // When the action finishes, re-fetch policy to reflect any changes
        if (!inProgress) {
          getPolicy(params.policyId, {}, clientId).then(
            ([updatedPolicy, err]) => {
              if (!err && updatedPolicy) {
                setCurrentPolicy(updatedPolicy);
                if (policyTerm === "current") {
                  setPolicy(updatedPolicy);
                  applyCalloutForPolicy(updatedPolicy, setCallout);
                  if (validationTriggerRef.current) {
                    validationTriggerRef.current();
                  }
                }
              }
            }
          );
        }
      }
    );
    return unsubscribe;
  }, [params.policyId, clientId, policyTerm]);

  const fetchData = async (policyId) => {
    const [
      [fetchedCurrentPolicy, currentPolicyError],
      [fetchedRenewalPolicy, renewalPolicyError],
      [fetchedPolicyActions, policyActionsError],
    ] = await Promise.all([
      getPolicy(policyId, { term: "current" }, clientId),
      getPolicy(policyId, { term: "renewal" }, clientId),
      getAvailablePolicyActions(policyId, clientId),
    ]);

    if (get(currentPolicyError, "code") === 403) {
      handleForbiddenError(currentPolicyError);
      return;
    }

    // A 404 means the term simply doesn't exist for this policy, so it drops
    // its tab rather than failing the page. Anything else is a real error.
    if (currentPolicyError && get(currentPolicyError, "code") !== 404) {
      console.error("Error fetching policy details:", currentPolicyError);
    }
    if (renewalPolicyError && get(renewalPolicyError, "code") !== 404) {
      console.error(
        "Error fetching renewal policy details:",
        renewalPolicyError
      );
    }

    let availableCurrentPolicy = currentPolicyError
      ? undefined
      : fetchedCurrentPolicy;
    const availableRenewalPolicy = renewalPolicyError
      ? null
      : fetchedRenewalPolicy;

    // Neither term exists; the stored policy is still viewable without a term filter.
    if (
      get(currentPolicyError, "code") === 404 &&
      get(renewalPolicyError, "code") === 404
    ) {
      const [fetchedDefaultPolicy, defaultPolicyError] = await getPolicy(
        policyId,
        {},
        clientId
      );
      if (defaultPolicyError) {
        console.error("Error fetching policy details:", defaultPolicyError);
      } else {
        availableCurrentPolicy = fetchedDefaultPolicy;
      }
    }

    setCurrentPolicy(availableCurrentPolicy);
    setRenewalPolicy(availableRenewalPolicy ?? null);

    // Open on whichever term exists, so a missing current term still renders.
    const activeTerm = availableCurrentPolicy ? "current" : "renewal";
    const activePolicy = availableCurrentPolicy ?? availableRenewalPolicy;

    if (!activePolicy) {
      return;
    }

    setPolicyTerm(activeTerm);
    setPolicy(activePolicy);

    // Build display details from each action's displayConfig
    if (policyActionsError) {
      console.error("Error fetching policy actions:", policyActionsError);
    } else {
      const allActions = fetchedPolicyActions || [];

      const validationActions = [];
      const sectionActionsBySection = {};

      allActions.forEach((action) => {
        const displayConfig = action.displayConfig;

        if (!displayConfig) {
          console.warn(
            `No display config found for action type: ${action.type}`
          );
          return;
        }

        // Process validation displays
        if (displayConfig.validations && displayConfig.validations.length > 0) {
          displayConfig.validations.forEach((validation) => {
            validationActions.push({
              type: action.type,
              label: validation.label,
              confirmationMessage: validation.confirmationMessage,
              display: {
                location: VALIDATION_LOCATION,
                rule: validation.rule,
                showOnStatus: validation.showOnStatus,
              },
            });
          });
        }

        // Process section displays
        if (displayConfig.sections && displayConfig.sections.length > 0) {
          displayConfig.sections.forEach((section) => {
            const sectionAction = {
              type: action.type,
              label: section.label,
              confirmationMessage: section.confirmationMessage,
              display: {
                location: SECTION_LOCATION,
                section: section.section,
              },
            };

            // Group by section name
            if (!sectionActionsBySection[section.section]) {
              sectionActionsBySection[section.section] = [];
            }
            sectionActionsBySection[section.section].push(sectionAction);
          });
        }
      });

      setPolicyActions({
        validation: validationActions,
        sectionsByName: sectionActionsBySection,
      });
    }

    if (activePolicy.user) {
      const [fetchedUser, userError] = await getUser(
        activePolicy.user,
        clientId
      );
      if (userError) {
        console.error("Error fetching user details:", userError);
      }
      setUser(fetchedUser);
    }

    if (activeTerm === "current") {
      applyCalloutForPolicy(activePolicy, setCallout);
    }

    // Resume monitoring any pending policy actions (shows toasts, polls, disables buttons)
    resumePendingPolicyActions(
      policyId,
      clientId,
      handlePolicyRefreshSuccess,
      {}
    );

    return;
  };

  useEffect(() => {
    setPolicyTerm("current");
    setCurrentPolicy(undefined);
    setRenewalPolicy(null);
    setPolicy(undefined);
    cachedValidationRulesRef.current = {};
    setValidationRules({});
    if (!client) {
      return;
    }
    setIsLoading(true);
    trackPromise(fetchData(params.policyId).then(() => setIsLoading(false)));
  }, [client, params.policyId]);

  const renderDetails = policy && (
    <DetailsPage
      getPolicyReportOptions={() =>
        policyTermRef.current === "renewal" ? { term: "renewal" } : {}
      }
      title={policy.id}
      badge={
        policy.isActive === true
          ? "active"
          : policy.isActive === false
          ? "inactive"
          : undefined
      }
      actions={[
        {
          type: "copy",
          label: "Copy ID",
          value: policy.id,
        },
        {
          type: "download",
          label: "Download Report",
          value: params.policyId,
        },
      ]}
    >
      {/* Primary column */}
      <DetailsPageColumn primary>
        {callout && <Callout type={callout.type} message={callout.message} />}
        {size(availableTerms) > 1 && (
          <PolicyTermSelector
            isActionInProgress={isActionInProgress}
            policyTerm={policyTerm}
            availableTerms={availableTerms}
            handleTermChange={handleTermChange}
          />
        )}
        {/* Policy details */}
        <Policy
          policy={policy}
          sectionActionsByName={policyActions.sectionsByName}
          onPolicyRefreshSuccess={handlePolicyRefreshSuccess}
          isActionInProgress={isActionInProgress}
          validationRules={validationRules}
          policyTerm={policyTerm}
        />
      </DetailsPageColumn>

      {/* Secondary column */}
      <DetailsPageColumn>
        {/* User */}
        {user && (
          <DetailsPageCard title="User">
            <div className="flex flex-col divide-y gap-y-2">
              <div className="flex flex-col">
                <h3 className="uppercase text-[0.625rem] font-medium">Name</h3>
                <p className="text-sm">
                  {/* This should be replaced with ignitionSession info*/}
                  {startCase(user.firstName)} {startCase(user.lastName)}
                </p>
              </div>
              <div className="flex flex-col pt-2">
                <h3 className="uppercase text-[0.625rem] font-medium">Phone</h3>
                <p className="text-sm">{user.phone}</p>
              </div>
              <div className="flex flex-col pt-2">
                <h3 className="uppercase text-[0.625rem] font-medium">Email</h3>
                <p className="text-sm">{user.email ?? "—"}</p>
              </div>
            </div>
            {/* This should be filled in with information from the user object */}
          </DetailsPageCard>
        )}
        {get(client, "config.validationConfig.enabled") && (
          <div className={policyTerm !== "current" ? "hidden" : undefined}>
            <PolicyValidation
              title="Policy Validation"
              iconStatus="shimmer"
              client={client}
              policyId={policy.id}
              accountId={policy.account}
              validationActions={policyActions.validation}
              onPolicyRefreshSuccess={handlePolicyRefreshSuccess}
              onValidationResults={handleValidationResults}
              isActionInProgress={isActionInProgress}
              validationTriggerRef={validationTriggerRef}
            />
          </div>
        )}
      </DetailsPageColumn>
    </DetailsPage>
  );

  return <>{isLoading ? <LoadingSpinner /> : renderDetails}</>;
};

export default PolicyDetails;
